import { useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { refreshUser } = useContext(AuthContext);

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Session ID não encontrado');
          navigate('/client/login');
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Determine user type from current path
        const userType = location.pathname.includes('/provider') ? 'provider' : 'client';
        
        // Google Auth não está disponível para prestadores (decisão de produto Fev/2026).
        // Bloqueia o fluxo caso alguém acesse /provider/* com session_id no hash
        // (links antigos, callback do Emergent Auth, etc.).
        if (userType === 'provider') {
          toast.error('Cadastro/login com Google não está disponível para prestadores. Use seu e-mail e senha.');
          navigate('/provider/login', { replace: true });
          return;
        }
        
        // Call backend to exchange session_id for user data
        const response = await axios.post(
          `${API}/api/auth/google/session`,
          { session_id: sessionId, user_type: userType },
          { withCredentials: true }
        );

        const { user, token, session_token, needs_completion } = response.data;

        // Set cookie (backend should also set it via Set-Cookie header)
        document.cookie = `session_token=${session_token}; path=/; max-age=604800; SameSite=None; Secure`;

        // Persiste JWT no localStorage para que endpoints legados que dependem do header
        // Authorization (ex.: /provider/onboarding/*, ProviderLayout) funcionem para
        // usuários Google. O cookie session_token continua sendo o fallback.
        if (token) {
          localStorage.setItem('token', token);
        }

        // Hidratar AuthContext via /api/auth/me (usa cookie session_token via fallback)
        // Sem isso, páginas protegidas (ex: /client/vehicle) veem user=null e redirecionam ao login
        await refreshUser();

        if (needs_completion) {
          // User needs to complete profile (add CPF/CNPJ)
          toast.success(`Bem-vindo, ${user.name}! Por favor, complete seu cadastro.`);
          
          if (userType === 'client') {
            navigate('/client/complete-profile', { state: { user } });
          } else {
            navigate('/provider/complete-profile', { state: { user } });
          }
        } else {
          // User already has complete profile
          toast.success(`Bem-vindo de volta, ${user.name}!`);
          
          if (userType === 'client') {
            navigate('/client/dashboard', { state: { user } });
          } else {
            navigate('/provider/dashboard', { state: { user } });
          }
        }
      } catch (error) {
        console.error('Google auth error:', error);
        toast.error(error.response?.data?.detail || 'Erro ao autenticar com Google');
        navigate('/client/login');
      }
    };

    processGoogleAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Autenticando com Google...</p>
      </div>
    </div>
  );
}
